import { Link } from 'react-router-dom';
import { MapPin, TreePine, Droplets, AlertTriangle, Shield, Users, Vote, Eye, Clock, Check, ChevronRight, Scale, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const priorityZones = [
  { name: 'St. Lucie County', designated: '2026-01-15', threat: 'Development / Environmental', activeCases: 2, members: 45, resources: 'Legal, Advocacy, Land Acquisition', region: 'Treasure Coast', founding: true },
  { name: 'Martin County', designated: '2026-01-15', threat: 'Environmental / Waterway', activeCases: 1, members: 32, resources: 'Legal, Environmental Monitoring', region: 'Treasure Coast', founding: true },
  { name: 'Indian River County', designated: '2026-01-15', threat: 'Development / Wildlife', activeCases: 1, members: 28, resources: 'Legal, Wildlife Corridor Protection', region: 'Treasure Coast', founding: true },
  { name: 'Okeechobee County', designated: '2026-02-01', threat: 'Environmental / Water Quality', activeCases: 0, members: 15, resources: 'Advocacy, Environmental Monitoring', region: 'Treasure Coast', founding: true },
];

const watchCounties = [
  { name: 'Levy County', threat: 'High', category: 'Domestic Violence / Development' },
  { name: 'Dixie County', threat: 'High', category: 'Domestic Violence / Environmental' },
  { name: 'Marion County', threat: 'High', category: 'Domestic Violence / Development' },
  { name: 'Taylor County', threat: 'High', category: 'Domestic Violence / Environmental' },
  { name: 'DeSoto County', threat: 'High', category: 'Domestic Violence / Development' },
  { name: 'Hardee County', threat: 'High', category: 'Domestic Violence / Environmental' },
  { name: 'Duval County', threat: 'Medium', category: 'Domestic Violence / Urban Development' },
  { name: 'Pasco County', threat: 'Medium', category: 'Domestic Violence / Rapid Growth' },
  { name: 'Leon County', threat: 'Medium', category: 'Domestic Violence / Policy' },
  { name: 'Holmes County', threat: 'High', category: 'Domestic Violence / Rural Community' },
];

const designationSteps = [
  { step: 1, title: 'Nominate', desc: 'Any member can nominate a county or municipality for Priority Zone status.' },
  { step: 2, title: 'Research', desc: 'Safe Florida researches and publishes a full community threat assessment.' },
  { step: 3, title: 'Vote', desc: 'Founding Members and Seat at the Table members vote.' },
  { step: 4, title: 'Designate', desc: 'Zones receiving majority vote are designated for 12 months.' },
  { step: 5, title: 'Review', desc: 'Zone status is reviewed and re-voted annually.' },
];

export default function PriorityZones() {
  return (
    <div className="pt-16" data-testid="priority-zones-page">
      {/* Header */}
      <section className="bg-forest py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold font-body mb-3">Priority Zones</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4 leading-tight">
              Safe Florida Priority Zones — Where We Fight Hardest
            </h1>
            <p className="text-lg text-white/90 font-body italic">
              Member-voted. Community-driven. Transparently deployed.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 1 — WHAT IS A PRIORITY ZONE */}
      <section className="py-20 bg-white" data-testid="pz-what-is">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="border-l-4 border-forest pl-6">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy mb-6">What Is a Priority Zone</h2>
            <p className="text-base text-gray-700 font-body leading-relaxed">
              A Priority Zone is a county or municipality that Safe Florida members have voted to designate as a concentrated focus area. In a Priority Zone, Safe Florida deploys <strong className="text-navy">maximum resources</strong> — legal, advocacy, land acquisition, and community organizing — to protect that community.
            </p>
          </div>
          <div className="grid sm:grid-cols-4 gap-4 mt-10">
            {[
              { icon: Scale, label: 'Legal', desc: 'Defense fund deployment' },
              { icon: Users, label: 'Advocacy', desc: 'Community organizing' },
              { icon: TreePine, label: 'Land', desc: 'Acquisition & preservation' },
              { icon: Shield, label: 'Protection', desc: 'Maximum resource allocation' },
            ].map(({ icon: Icon, label, desc }) => (
              <div key={label} className="text-center border border-gray-200 rounded-lg p-5 card-hover">
                <Icon className="w-8 h-8 text-forest mx-auto mb-2" />
                <p className="font-heading font-bold text-navy text-sm">{label}</p>
                <p className="text-xs text-gray-500 font-body mt-1">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2 — HOW A ZONE IS DESIGNATED */}
      <section className="py-20 bg-[#FAFAFA]" data-testid="pz-how-designated">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <Vote className="w-10 h-10 text-navy mx-auto mb-3" />
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy mb-3">How a Zone Is Designated</h2>
            <p className="text-base text-gray-600 font-body">A transparent, member-driven designation process.</p>
          </div>
          <div className="grid md:grid-cols-5 gap-3">
            {designationSteps.map(({ step, title, desc }) => (
              <div key={step} className="bg-white border border-gray-200 rounded-lg p-5 text-center relative" data-testid={`designation-step-${step}`}>
                <div className="w-10 h-10 bg-navy rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-gold font-heading font-bold">{step}</span>
                </div>
                <h3 className="font-heading font-bold text-navy text-sm mb-2">{title}</h3>
                <p className="text-xs text-gray-600 font-body leading-relaxed">{desc}</p>
                {step < 5 && <ChevronRight className="hidden md:block absolute top-1/2 -right-3 w-5 h-5 text-gold -translate-y-1/2 z-10" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — CURRENT PRIORITY ZONES MAP */}
      <section className="py-20 bg-white" data-testid="pz-current-zones">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <MapPin className="w-10 h-10 text-forest mx-auto mb-3" />
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy mb-3">Current Priority Zones</h2>
            <p className="text-base text-gray-600 font-body">Founding Priority Zones — Treasure Coast Region</p>
          </div>

          {/* Florida Map Visualization */}
          <div className="bg-forest/5 border-2 border-forest/20 rounded-xl p-8 mb-10 relative overflow-hidden">
            <div className="flex items-center justify-center mb-6">
              <div className="relative">
                {/* Simplified FL map representation */}
                <svg viewBox="0 0 400 500" className="w-64 h-80 mx-auto">
                  <path d="M120,50 L280,50 L290,60 L300,80 L310,100 L320,130 L340,150 L350,170 L355,200 L350,230 L340,260 L320,290 L300,320 L280,340 L260,370 L240,400 L220,430 L200,450 L180,440 L160,420 L140,390 L120,350 L100,310 L90,270 L85,230 L90,190 L100,150 L110,110 L115,80 Z"
                    fill="#2D5F2D" fillOpacity="0.15" stroke="#2D5F2D" strokeWidth="2" />
                  {/* Priority Zone markers */}
                  <circle cx="300" cy="280" r="12" fill="#D4AF37" stroke="#1B3A6B" strokeWidth="2" />
                  <circle cx="310" cy="250" r="10" fill="#D4AF37" stroke="#1B3A6B" strokeWidth="2" />
                  <circle cx="305" cy="310" r="10" fill="#D4AF37" stroke="#1B3A6B" strokeWidth="2" />
                  <circle cx="270" cy="300" r="10" fill="#D4AF37" stroke="#1B3A6B" strokeWidth="2" />
                  <text x="320" y="285" fontSize="8" fill="#1B3A6B" fontWeight="bold">St. Lucie</text>
                  <text x="320" y="255" fontSize="8" fill="#1B3A6B" fontWeight="bold">Indian River</text>
                  <text x="315" y="315" fontSize="8" fill="#1B3A6B" fontWeight="bold">Martin</text>
                  <text x="230" y="305" fontSize="8" fill="#1B3A6B" fontWeight="bold">Okeechobee</text>
                </svg>
              </div>
            </div>
            <div className="flex justify-center gap-6 text-xs font-body">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-gold border border-navy inline-block"></span> Active Priority Zone</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-400 border border-red-600 inline-block"></span> Watch County</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-forest/20 border border-forest inline-block"></span> Florida Territory</span>
            </div>
          </div>

          {/* Zone Cards */}
          <div className="grid md:grid-cols-2 gap-4">
            {priorityZones.map(zone => (
              <div key={zone.name} className="border-2 border-forest/30 bg-forest/5 rounded-lg p-5" data-testid={`zone-card-${zone.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-forest" />
                    <h3 className="font-heading font-bold text-navy text-base">{zone.name}</h3>
                  </div>
                  <div className="flex gap-1">
                    <Badge className="bg-gold/20 text-gold text-xs font-body">ACTIVE</Badge>
                    {zone.founding && <Badge className="bg-navy/10 text-navy text-xs font-body">FOUNDING</Badge>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs font-body text-gray-700">
                  <p><strong>Designated:</strong> {new Date(zone.designated).toLocaleDateString()}</p>
                  <p><strong>Threat:</strong> {zone.threat}</p>
                  <p><strong>Active Cases:</strong> {zone.activeCases}</p>
                  <p><strong>Members:</strong> {zone.members}</p>
                </div>
                <p className="text-xs font-body text-gray-600 mt-2"><strong>Resources:</strong> {zone.resources}</p>
                <p className="text-xs font-body text-gray-500 mt-1">Region: {zone.region}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — COUNTIES UNDER WATCH */}
      <section className="py-20 bg-navy" data-testid="pz-watch-counties">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <AlertTriangle className="w-10 h-10 text-gold mx-auto mb-3" />
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-3">Counties Currently Under Watch</h2>
            <p className="text-base text-white/70 font-body">Top 10 domestic violence counties plus Treasure Coast founding zones.</p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {watchCounties.map(county => (
              <div key={county.name} className="bg-white/10 border border-white/20 rounded-lg p-4" data-testid={`watch-county-${county.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-body font-bold text-white text-sm">{county.name}</h4>
                  <Badge className={`text-xs ${county.threat === 'High' ? 'bg-red-500/30 text-red-300' : 'bg-gold/30 text-gold'}`}>{county.threat}</Badge>
                </div>
                <p className="text-xs text-white/60 font-body">{county.category}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 p-4 bg-white/10 border border-white/20 rounded-lg text-center">
            <p className="text-sm text-white/80 font-body">
              <strong className="text-gold">Treasure Coast Region</strong> (St. Lucie, Martin, Indian River, Okeechobee) designated as <strong className="text-gold">Founding Priority Zones</strong>
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 5 — TRANSPARENCY COMMITMENT */}
      <section className="py-20 bg-white" data-testid="pz-transparency">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-8">
            <Eye className="w-10 h-10 text-forest mx-auto mb-3" />
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy mb-6">Transparency Commitment</h2>
          </div>
          <div className="bg-[#FAFAFA] border-2 border-forest/20 rounded-lg p-8">
            <p className="text-base text-gray-700 font-body leading-relaxed mb-6">
              Every Priority Zone designation, every resource deployment, and every outcome is published publicly on this page. Safe Florida members can track exactly where their money goes and what results it produces.
            </p>
            <p className="text-lg text-navy font-body font-bold mb-6">
              No closed doors. No hidden decisions. Full accountability to every member.
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { icon: MapPin, label: 'Zone Designations', desc: 'Published in real time' },
                { icon: DollarSign, label: 'Resource Deployment', desc: 'Every dollar tracked' },
                { icon: Check, label: 'Outcomes', desc: 'Results published publicly' },
              ].map(({ icon: Icon, label, desc }) => (
                <div key={label} className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg">
                  <Icon className="w-5 h-5 text-forest flex-shrink-0" />
                  <div>
                    <p className="font-body font-bold text-navy text-sm">{label}</p>
                    <p className="text-xs text-gray-500 font-body">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-navy">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <MapPin className="w-12 h-12 text-gold mx-auto mb-4" />
          <h2 className="text-3xl font-heading font-bold text-white mb-4">Vote on the Next Priority Zone</h2>
          <p className="text-lg text-white/90 font-body mb-8">
            Founding Members have full voting rights on Priority Zone designations. Join today and decide where Safe Florida fights next.
          </p>
          <Link to="/programs/membership" data-testid="pz-cta-founding">
            <Button size="lg" className="bg-gold text-navy hover:bg-gold/90 font-body font-bold px-10 rounded-md">
              BECOME A FOUNDING MEMBER
            </Button>
          </Link>
          <p className="font-signature text-white/80 text-2xl mt-8">Where We Fight Hardest. — F.C. Clark</p>
        </div>
      </section>
    </div>
  );
}
